import { Button, DakenService, DataHelper, LogService, ProfileAdditionalInfo, ToastHelper, User, UserHelper } from '@hub/shared'
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'
import ReactCodeInput from 'react-code-input'

import { SqCallout, SqOverlay } from '@squidit/react-css'
import { useTranslation } from 'react-i18next'
import './phone-validation.component.scoped.scss'
import './phone-validation.component.scss'

// ? O tempo é medido em milissegundos.
// ? 60 segundos * 2 = 120 segundos
// ? Para transformar em milissegundos, basta multiplicar por 1000
const TIMEOUT = 60 * 2 * 1000

interface Props {
  register: ProfileAdditionalInfo
  setRegister: (value: ProfileAdditionalInfo) => void
  open?: boolean
  setOpen: (value: boolean) => void
}

type TypeChannel = 'sms' | 'whatsapp'

export default ({ open = true, setOpen, register, setRegister }: Props) => {
  const dakenService = useMemo(() => new DakenService(), [])
  const toast = useMemo(() => new ToastHelper().toast, [])
  const logService = useMemo(() => new LogService(), [])
  const dataHelper = useMemo(() => new DataHelper(), [])
  const userHelper = useMemo(() => new UserHelper(), [])
  logService.init('PhoneValidation')

  const [user, setUser] = useState<User | null>(null)
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingWhatsapp, setLoadingWhatsapp] = useState(false)
  const [loadingSMS, setLoadingSMS] = useState(false)
  const [key, setKey] = useState(0)
  const [firstSend, setFirstSend] = useState<boolean>(true)

  const { t } = useTranslation('phoneValidation')

  const validatePhoneCode = useCallback(
    async (code: string) => {
      try {
        return await dakenService.putPhoneVerifyCode(code)
      } catch (err) {
        logService.error({ message: 'Failed to validate code', error: err })
        return err
      }
    },
    [logService, dakenService]
  )

  const sendVerifyPhoneCode = useCallback(
    async (type: TypeChannel = 'sms') => {
      try {
        await dakenService.postPhoneValidationCode(type)
        logService.info({ message: 'Code sent successfully' })
      } catch (err) {
        logService.error({ message: 'Failed to send validation code', error: err })
        throw err
      }
    },
    [logService, dakenService]
  )

  const sendVerificationCode = useCallback(
    async (type: TypeChannel = 'sms') => {
      let aux: Dispatch<SetStateAction<boolean>>
      try {
        if (type === 'sms') {
          setLoadingSMS(true)
        } else {
          setLoadingWhatsapp(true)
        }
        await sendVerifyPhoneCode(type)
        setTimeout(() => {
          aux(false)
        }, TIMEOUT)

        if (!firstSend) {
          toast.success(t('codeSendToast'), {
            closeButton: true,
            className: 'full text-bold',
            position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
            notOverClick: true,
            immediately: true,
          })
        }

        setFirstSend(false)
      } catch (err) {
        logService.error({ message: 'Failed to send code', error: err })
        toast.error(t('codeSendFailed'), {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 3600000,
          notOverClick: true,
          immediately: true,
        })
      }
    },
    [logService, sendVerifyPhoneCode, toast, t, firstSend]
  )

  const checkVerificationCode = useCallback(async () => {
    try {
      setLoading(true)
      const response = await validatePhoneCode(code)
      setLoading(false)
      if (response === true) {
        const newUser: User | any = { ...user, profileadditionalinfo: { ...user?.profileadditionalinfo, phoneValid: 1 } }
        setUser(newUser)
        await userHelper.setUser(newUser)
        toast.success(t('phoneValidated'), {
          closeButton: true,
          className: 'full text-bold',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          notOverClick: true,
          immediately: true,
        })
        setOpen(false)
      } else {
        toast.error(t('invalidCode'), {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 3600000,
          notOverClick: true,
          immediately: true,
        })
      }
    } catch (error) {
      toast.error(t('invalidCode'), {
        closeButton: true,
        className: 'full',
        position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
        duration: 3600000,
        notOverClick: true,
        immediately: true,
      })
    } finally {
      setCode('')
      setKey((prevKey) => prevKey + 1)
    }
  }, [code, setOpen, t, toast, user, userHelper, validatePhoneCode])

  const ActionButtons = () => (
    <div className="actions">
      <Button
        rounded
        color="var(--background_secondary)"
        borderColor="var(--text_color)"
        textColor="var(--text_color)"
        onClick={() => sendVerificationCode('whatsapp')}
        disabled={loadingWhatsapp}
      >
        <span className="text-button-action">
          <i className="fa-brands fa-whatsapp" style={{ color: 'var(--whatsapp)' }}></i>
          {t('viaWhatsapp')}
        </span>
      </Button>
      <Button
        rounded
        color="var(--background_secondary)"
        borderColor="var(--text_color)"
        textColor="var(--text_color)"
        onClick={() => sendVerificationCode('sms')}
        disabled={loadingSMS}
      >
        <span className="text-button-action">
          <i className="fa-regular fa-message"></i>
          {t('viaSms')}
        </span>
      </Button>
    </div>
  )

  const Footer = () => (
    <div className="footer">
      <Button
        onClick={() => setOpen(false)}
        color="var(--background_secondary)"
        inverted
        textColor="var(--text_color)"
        borderColor="var(--text_color)"
      >
        {t('toCancel')}
      </Button>
      <Button onClick={checkVerificationCode} loading={loading} disabled={loading || !code || code.length < 6}>
        {t('toValidate')}
      </Button>
    </div>
  )

  useEffect(() => {
    if (open) {
      sendVerificationCode('whatsapp')
    } else {
      setFirstSend(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    userHelper.getUser().then((user) => {
      setUser(user)
    })
  }, [userHelper, open])

  useEffect(() => {
    if (code?.length === 6) {
      checkVerificationCode()
    }
  }, [code, checkVerificationCode])

  return (
    <SqOverlay
      onCloseChange={() => {
        setOpen(false)
      }}
      onOpenChange={(open: boolean) => setOpen(open)}
      open={open}
      header={<h5 className="title-phone-validation">{t('title')}</h5>}
      footer={<Footer />}
    >
      <section className="container">
        <h3
          className="text-medium code-send"
          dangerouslySetInnerHTML={{
            __html: t('sendMessage', {
              phoneNumber: register?.phone?.slice(0, 2) === '55' ? dataHelper.formatPhoneNumber(register?.phone) : register?.phone,
            }),
          }}
        />
        <ReactCodeInput
          type="text"
          fields={6}
          name="code-validation"
          inputMode="numeric"
          value={code}
          onChange={(value: string) => setCode(value)}
          key={key}
          inputStyle={{
            width: '3.2rem',
            height: '3.5rem',
            border: '1px solid var(--gray)',
            margin: '4px',
            borderRadius: '8px',
            textAlign: 'center',
            fontSize: '1.5rem',
            fontWeight: '300',
          }}
          className="code-input"
        />
        <div className="message" dangerouslySetInnerHTML={{ __html: t('message') }} />
        {loadingSMS || loadingWhatsapp ? (
          <SqCallout
            colorIcon="var(--text_color)"
            className="box-shadow mb-6"
            backgroundColor="var(--background_secondary)"
            iconFontSize="34px"
            icon={'fa-solid fa-lightbulb-on'}
            type="warning"
          >
            {t('waitMessage')}
          </SqCallout>
        ) : null}
        <ActionButtons />
      </section>
    </SqOverlay>
  )
}
